import React from 'react';
import {
  IRealEstate,
  ILandParcelEntry,
} from '../../../../../../Interfaces/IRealEstate';
import { Grid, Typography } from '@mui/material';
import { checkString, formatDate } from '../../../../../core/standardFunctions';
import { IRealEstateHasPropertyType } from '../../../../../../Interfaces/IRealEstateHasPropertyType';
import { IPropertyType } from '../../../../../../Interfaces/IPropertyType';
import { IRealEstateHasInventoryOption } from '../../../../../../Interfaces/IRealEstateHasInventoryOption';
import { purchaseContractRelationTypes } from '../../../../../../constants';
import { PurchaseContractRelationSummary } from './PurchaseContractRelationSummary';
import { IPurchaseContractHasPerson } from '../../../../../../Interfaces/IPerson';

interface IProps {
  realEstateArray: IRealEstate[];
  realEstateHasPropertyTypeArray: IRealEstateHasPropertyType[];
  realEstateHasInventroyOptionArray: IRealEstateHasInventoryOption[];
  propertyTypeArray: IPropertyType[];
  personArray: IPurchaseContractHasPerson[];
}

export const RealEstateSummary: React.FC<IProps> = ({
  realEstateArray,
  realEstateHasInventroyOptionArray,
  realEstateHasPropertyTypeArray,
  propertyTypeArray,
  personArray,
}) => {
  const getHasPropertyType = (idRealEstate: number) => {
    const filteredIds = realEstateHasPropertyTypeArray
      .filter(x => x.idRealEstate === idRealEstate)
      .map(x => x.idPropertyType);

    return propertyTypeArray
      .filter(pt => filteredIds.includes(pt.idPropertyType))
      .map(pt => pt.PropertyType)
      .join(', ');
  };

  const getRelationName = (idPurchaseRealtionType: number | null) => {
    const foundObject = purchaseContractRelationTypes.find(
      x => x.idPurchaseContractRelationType === Number(idPurchaseRealtionType)
    );
    return foundObject ? foundObject.PurchaseContractRelationType : 'k.A.';
  };

  const getPropertyTsx = (idRealEstate: number) => {
    const filteredElements = realEstateHasInventroyOptionArray.filter(
      x => x.idRealEstate === idRealEstate
    );
    return filteredElements.map((x, idx) => (
      <React.Fragment key={`inventory-${idx}`}>
        <Grid item sm={2} />
        <Grid item sm={2}>
          {x.Amount}
        </Grid>
        <Grid item sm={4}>
          {x.Title}
        </Grid>
        <Grid item sm={4}>
          {checkString(x.Description)}
        </Grid>
      </React.Fragment>
    ));
  };

  return (
    <>
      <Typography variant='h6'>Zusammenfassung</Typography>
      <Grid container spacing={2} sx={{ ml: 5, mt: 2 }}>
        {realEstateArray.map((x, i) => (
          <React.Fragment key={`re-${x.idRealEstate}-${i}`}>
            {i !== 0 && (
              <Grid item xs={12}>
                <hr />
              </Grid>
            )}

            {realEstateArray.length > 1 && (
              <>
                <Grid item sm={4}>
                  <b>Nr.:</b>
                </Grid>
                <Grid item sm={8}>
                  {i + 1}
                </Grid>
              </>
            )}

            <Grid item sm={4}>
              <b>Kaufpreis Objekt:</b>
            </Grid>
            <Grid item sm={8}>
              {x.Price.toLocaleString('de-DE', {
                style: 'currency',
                currency: 'EUR',
              })}
            </Grid>

            {realEstateHasInventroyOptionArray.some(
              y => y.idRealEstate === x.idRealEstate
            ) && (
              <>
                <Grid item sm={4}>
                  <b>Kaufpreis Anlagen bzw. Inventar:</b>
                </Grid>
                <Grid item sm={8}>
                  {x.PriceInventoryOption === null
                    ? '0,00 €'
                    : x.PriceInventoryOption.toLocaleString('de-DE', {
                        style: 'currency',
                        currency: 'EUR',
                      })}
                </Grid>
              </>
            )}

            <Grid item sm={4}>
              <b>Adresse:</b>
            </Grid>
            <Grid item sm={8}>
              {checkString(x.Street)} {checkString(x.StreetNr)}
            </Grid>
            <Grid item sm={4} />
            <Grid item sm={8}>
              {checkString(x.Postcode)} {checkString(x.City)}
            </Grid>

            {/* Neuer Abschnitt: Grundbuch‐Einträge */}
            {Array.isArray(x.LandParcelEntryArray) &&
            x.LandParcelEntryArray.length > 0 ? (
              <>
                {/* Divider VOR dem ersten Eintrag */}
                <Grid item xs={12}>
                  <hr />
                </Grid>
                {x.LandParcelEntryArray.map(
                  (eintrag: ILandParcelEntry, idx: number) => (
                    <React.Fragment key={`gb-${x.idRealEstate}-${idx}`}>
                      {/* Divider zwischen Einträgen */}
                      {idx > 0 && (
                        <Grid item xs={12}>
                          <hr />
                        </Grid>
                      )}

                      {/* Überschrift pro Eintrag */}
                      <Grid item sm={12}>
                        <Typography
                          variant='subtitle1'
                          sx={{ mt: idx === 0 ? 2 : 1 }}
                        >
                          Grundbuch‐Eintrag {idx + 1}:
                        </Typography>
                      </Grid>

                      {/* Amtsgericht */}
                      <Grid item sm={4}>
                        <b>Amtsgericht:</b>
                      </Grid>
                      <Grid item sm={8}>
                        {eintrag.Court
                          ? `${checkString(eintrag.Court.Court)} | ${checkString(
                              eintrag.Court.FederalState
                            )}`
                          : 'k.A.'}
                      </Grid>

                      {/* Grundbuch / Blatt */}
                      <Grid item sm={4}>
                        <b>Grundbuch / Blatt:</b>
                      </Grid>
                      <Grid item sm={8}>
                        {`${checkString(eintrag.LandRegister)} / ${checkString(
                          eintrag.SheetLandRegister
                        )}`}
                      </Grid>

                      {/* Flur */}
                      <Grid item sm={4}>
                        <b>Flur:</b>
                      </Grid>
                      <Grid item sm={8}>
                        {checkString(eintrag.Parcel)}
                      </Grid>

                      {/* Flurstück */}
                      <Grid item sm={4}>
                        <b>Flurstück:</b>
                      </Grid>
                      <Grid item sm={8}>
                        {checkString(eintrag.LandParcel)}
                      </Grid>
                    </React.Fragment>
                  )
                )}
                {/* Divider NACH dem letzten Eintrag */}
                <Grid item xs={12}>
                  <hr />
                </Grid>
              </>
            ) : (
              <>
                <Grid item sm={4}>
                  <b>Grundbuchinformationen:</b>
                </Grid>
                <Grid item sm={8}>
                  Keine Einträge
                </Grid>
              </>
            )}

            <Grid item sm={4}>
              <b>Grundstücksart(en):</b>
            </Grid>
            <Grid item sm={8}>
              {getHasPropertyType(x.idRealEstate)}
            </Grid>

            {(x.isBuild ||
              realEstateHasPropertyTypeArray
                .filter(y => y.idPropertyType === 1)
                .some(y => y.idRealEstate === x.idRealEstate)) && (
              <>
                <Grid item sm={4}>
                  <b>Bauten:</b>
                </Grid>
                <Grid item sm={8}>
                  {checkString(x.BuildWith)}
                </Grid>
              </>
            )}

            <Grid item sm={4}>
              <b>Wohn- oder Teileigentum?</b>
            </Grid>
            <Grid item sm={8}>
              {x.hasApartment ? 'Ja' : 'Nein'}
            </Grid>

            <Grid item sm={4}>
              <b>Verkauf Teilfläche?</b>
            </Grid>
            <Grid item sm={8}>
              {x.hasPartialArea ? 'Ja' : 'Nein'}
            </Grid>

            {x.hasPartialArea && (
              <>
                <Grid item sm={4}>
                  <b>Teilfläche bereits vermessen?</b>
                </Grid>
                <Grid item sm={8}>
                  {x.isMeasured ? 'Ja' : 'Nein'}
                </Grid>

                {x.isMeasured ? (
                  <>
                    <Grid item sm={4}>
                      <b>Fläche des Teilbereichs:</b>
                    </Grid>
                    <Grid item sm={8}>
                      {x.Area} qm
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item sm={4}>
                      <b>Beauftragung Vermessung:</b>
                    </Grid>
                    <Grid item sm={8}>
                      {getRelationName(
                        x.OrderMeasurement_idPurchaseContractRelationType
                      )}
                    </Grid>
                  </>
                )}

                <Grid item sm={4}>
                  <b>Kostenträger Vermessung:</b>
                </Grid>
                <Grid item sm={8}>
                  {getRelationName(
                    x.PaymentMeasurement_idPurchaseContractRelationType
                  )}
                </Grid>
              </>
            )}

            <Grid item sm={4}>
              <b>Dienstbarkeiten?</b>
            </Grid>
            <Grid item sm={8}>
              {x.hasEasement ? 'Ja' : 'Nein'}
            </Grid>

            {x.hasEasement && (
              <>
                <Grid item sm={4}>
                  <b>Erfasste Dienstbarkeiten:</b>
                </Grid>
                <Grid item sm={8}>
                  {checkString(x.EasementText)}
                </Grid>
              </>
            )}

            <Grid item sm={4}>
              <b>Anlagen bzw. Inventar?</b>
            </Grid>
            <Grid item sm={8}>
              {realEstateHasInventroyOptionArray.some(
                y => y.idRealEstate === x.idRealEstate
              )
                ? 'Ja'
                : 'Nein'}
            </Grid>
            {getPropertyTsx(x.idRealEstate)}

            <Grid item sm={4}>
              <b>Mängel / Einschränkungen?</b>
            </Grid>
            <Grid item sm={8}>
              {checkString(x.Defect, 'Nein')}
            </Grid>

            <Grid item sm={4}>
              <b>Besonderheiten?</b>
            </Grid>
            <Grid item sm={8}>
              {checkString(x.Particularities, 'Nein')}
            </Grid>

            <Grid item sm={4}>
              <b>Räumung notwendig?</b>
            </Grid>
            <Grid item sm={8}>
              {x.requiresClearance ? 'Ja' : 'Nein'}
            </Grid>

            <Grid item sm={4}>
              <b>Räumung bis:</b>
            </Grid>
            <Grid item sm={8}>
              {formatDate(x.clearanceDate)}
            </Grid>

            <Grid item sm={4}>
              <b>Vermietet/Verpachtet?</b>
            </Grid>
            <Grid item sm={8}>
              {x.isRented ? 'Ja' : 'Nein'}
            </Grid>

            <Grid item sm={12}>
              <PurchaseContractRelationSummary
                title='Mieter/Pächter'
                idPruchaseContractRelationType={15}
                personArray={x.rentedPersonArray}
              />
            </Grid>
          </React.Fragment>
        ))}
      </Grid>
    </>
  );
};
